<script lang="ts">
  // The activities dashboard for /activities and the my-fitness Activities tab. Built on the same
  // pieces as the workouts dashboard (see WorkoutsDash -> ChooseTrainingView), except that an
  // athlete's activity history is unbounded, so filtering stays on the server and the list grows by
  // appending pages as the athlete scrolls -- see ActivityFilterFields.
  //
  // Also drives the coach and squad dashboards via showOwner. Only the pickers (race selection,
  // training-load activity picker) still use ActivitiesDashLegacy -- they need selection/calendar
  // semantics this component doesn't have.
  import { getContext, onDestroy, onMount, setContext } from 'svelte';
  import ChooseActivityView from './Activities/Dashboard/ChooseActivityView.svelte';
  import ExerciseDialog from './MyFitness/Today/ExerciseDialog.svelte';
  import { ActivityFilterFields, WORKOUTS_ACTIVITY_TYPE, type ActivityOwnerOption } from './Activities/Dashboard/ActivityFilterFields';
  import { asActivityExercise } from './Activities/Dashboard/activityCardUtils';
  import type { Exercise } from './Common/ActivityFunctions';
  import type { TodaysActivities } from './Common/constants';
  import type { MyFitnessStores } from './MyFitness/stores';
  import { debouncer } from './Util/utils';
  import { activityTagsSaved, applyTagChange } from './Common/activityTagOptions';

  export let dataSource: string = '/activities/dashboard';
  export let perPage: number = 12;
  export let filterKey: string = 'activitiesDashFilter';
  /**
   * Coach and squad dashboards list other athletes' activities: cards name their owner, and the
   * details dialog is suppressed because everything in it (schedule, copy/move, hide, delete,
   * Strava, and the chart's own /activities/{path}/data fetch) is scoped to the calling user.
   * Those cards link out to the activity page instead.
   */
  export let showOwner: boolean = false;
  /**
   * The athletes this dashboard lists, for the member filter. The coach and squad pages already
   * know their own roster -- it is what scopes the endpoint -- so they hand it straight over rather
   * than making the dashboard fetch it. Left empty, the filter isn't offered at all.
   */
  export let owners: ActivityOwnerOption[] = [];
  /** What to call them: a coach has athletes, a squad has members. */
  export let ownersTitle: string = 'Athletes';
  /** /activities/favourites pins the favourites filter on; the athlete can't turn it off there. */
  export let forceFavOnly: boolean | string = false;
  /** Bottom edge of whatever else is stuck to the top of the host page -- see ChooseActivityView. */
  export let stickyTop: string = '46px';
  /**
   * What "Schedule a Ride" should do. On the my-fitness page that's switching to the planner tab;
   * left unset, the button just links there.
   */
  export let onScheduleActivity: (() => void) | null = null;

  // Mounted two ways: home.blade.php passes the stores as a plain prop, my_fitness.blade.php
  // supplies them through the Svelte context map. Re-set a prop as context so everything below
  // (ExerciseDialog, CommentsButton, ...) can read it via getContext regardless of the mount site.
  export let myFitness: MyFitnessStores | null = null;
  if (myFitness) setContext('myFitness', myFitness);
  const stores: MyFitnessStores | undefined = myFitness ?? getContext<MyFitnessStores>('myFitness');
  const trickleActive = stores?.trickleActive;

  let activities: Exercise[] = [];
  let totalResults = 0;
  /** Whether scrolling further can load more -- see load(). */
  let hasMore = false;
  let isLoading = true;
  let isLoadingMore = false;
  /** A live update is rebuilding the list underneath the athlete -- see resync(). */
  let isResyncing = false;
  /** Most activities one live update will re-derive in a single request -- see resync(). */
  const RESYNC_CAP = 120;
  let resyncsInFlight = 0;
  let loadFailed = false;

  // Filter edits outrun the network, so tag every request and ignore anything but the newest.
  let latestRequest = 0;

  const fields = new ActivityFilterFields(
    () => load(false),
    () => { fieldsProxy = fieldsProxy; },
    filterKey,
    Boolean(forceFavOnly),
    !showOwner
  );
  // A remembered selection can outlive the roster it was made against -- the filter key is shared by
  // every squad -- so drop anyone who isn't on this page's before the first request goes out.
  fields.pruneMembers(owners.map((owner) => owner.id));
  // ActivityFilterFields mutates itself in place; this is the assignment target that tells Svelte.
  let fieldsProxy = fields;

  /**
   * Fetch the next perPage activities. `append` distinguishes scrolling further down the same result
   * set (keep what's on screen, add below) from a new one (clear and start over), which also decides
   * whether the view shows skeletons or a spinner at the bottom.
   *
   * Appending asks for whatever comes after the cards already on screen (offset = how many there
   * are) rather than for page N: a card that has dropped out of the results since (deleted, or a
   * tag edit means it no longer matches) has left both lists, so nothing below it is skipped.
   */
  async function load(append: boolean) {
    const request = ++latestRequest;
    if (append) {
      isLoadingMore = true;
    } else {
      isLoading = true;
      activities = [];
    }
    loadFailed = false;
    try {
      const response = await window.fetch(`${dataSource}?${fields.toQuery(1, perPage, append ? activities.length : 0)}`);
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const paginator = await response.json();
      if (request !== latestRequest) return;
      const loaded: Exercise[] = (paginator.data ?? []).map(asActivityExercise);
      // An activity that arrived above the loaded cards since (an upload), or a deletion the server
      // hasn't finished yet, can make the next batch repeat one already on screen. The keyed each
      // block would throw on the duplicate.
      activities = append ? dropDuplicates([...activities, ...loaded]) : loaded;
      totalResults = paginator.total ?? activities.length;
      // A short batch is the end, even if the count says otherwise -- after an arrival above, the
      // cards on screen can stay one short of the total however far down the athlete scrolls.
      hasMore = loaded.length === perPage && activities.length < totalResults;
      stores?.loadCommentCounts(loaded.map((a) => a.path).filter((path): path is string => Boolean(path)));
    } catch (error) {
      if (request !== latestRequest) return;
      console.error('Failed to load activities', error);
      if (!append) {
        activities = [];
        totalResults = 0;
        hasMore = false;
      }
      loadFailed = true;
    } finally {
      if (request === latestRequest) {
        isLoading = false;
        isLoadingMore = false;
      }
    }
  }

  function dropDuplicates(list: Exercise[]): Exercise[] {
    const seen = new Set<string>();
    return list.filter((activity) => {
      const key = activity.path ?? '';
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  const loadMore = () => load(true);

  load(false);

  /**
   * Rebuild what's already on screen, in place: for changes that reach activities other than the one
   * the athlete just acted on. Nothing is torn down -- the list stays mounted, the scroll position
   * holds, and the cards below stay where they were -- because most of these arrive unprompted,
   * pushed by something finishing elsewhere, and being thrown back to the top of a list you were
   * reading is worse than the staleness it fixes.
   *
   * The window is re-requested as one oversized batch from the top rather than in pieces: the result
   * set has just changed underneath, so anything already on screen may have moved.
   */
  async function resync() {
    // A first page already on its way is about to replace the list anyway, and a page being appended
    // would be left half-applied: both clear their own flags only while they are the newest request,
    // so superseding one here would strand the skeletons or the infinite scroll.
    if (isLoading || isLoadingMore) return;
    // Each activity costs the endpoint real work (signature lookups, breakthrough state, thumbnail
    // checks), so an athlete who has scrolled a long way doesn't get all of it re-derived at once.
    // Past the cap only the head is rebuilt and the tail below it is kept as-is: under every sort
    // order an activity that was just uploaded, edited or deleted lands at the top, and a stale
    // card far down the list is a much smaller lie than a list that suddenly ends.
    const windowSize = Math.min(Math.max(perPage, activities.length), RESYNC_CAP);
    const request = ++latestRequest;
    resyncsInFlight++;
    isResyncing = true;
    try {
      const response = await window.fetch(`${dataSource}?${fields.toQuery(1, windowSize, 0)}`);
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const paginator = await response.json();
      if (request !== latestRequest) return;
      const loaded: Exercise[] = (paginator.data ?? []).map(asActivityExercise);
      const rebuilt = new Set(loaded.map((activity) => activity.path));
      const tail = activities.slice(windowSize).filter((activity) => !rebuilt.has(activity.path));
      activities = dropDuplicates([...loaded, ...tail]);
      totalResults = paginator.total ?? activities.length;
      // "Load more" resumes below the last card on screen, so how far down the athlete is now is
      // just whatever survived. A short window means everything matching is already here.
      hasMore = loaded.length === windowSize && activities.length < totalResults;
      loadFailed = false;
      stores?.loadCommentCounts(loaded.map((a) => a.path).filter((path): path is string => Boolean(path)));
    } catch (error) {
      // Leave the list alone. What's on screen is stale rather than wrong, and there is nothing for
      // the athlete to act on -- the next event, or their next filter change, tries again.
      console.error('Failed to resync activities', error);
    } finally {
      resyncsInFlight--;
      isResyncing = resyncsInFlight > 0;
    }
  }

  /**
   * The same events the Today page and the calendar listen to, so the dashboard is never a page
   * reload behind: an upload finishing, an activity edited or deleted from anywhere (another tab,
   * the activity page, a Strava webhook), or a squadmate's group ride attaching itself to one of
   * these rides.
   *
   * Debounced because one upload emits several of these in quick succession -- and because the
   * notification fires the instant the job finishes, so a moment's wait costs nothing and collapses
   * the burst into a single refetch.
   */
  const liveEvents = [
    'upload_activity_complete',
    'modify_activity_complete',
    'delete_activity_complete',
    // A delete sometimes reports itself as a schedule change (see DeleteActivity), and a planned
    // ride that has just been ridden stops being planned -- which is what puts it in this list.
    'schedule_activity_complete',
    'group_activity_update',
    // Trickle-down rewrites the fitness numbers on every activity it touched. The store below says
    // the same thing where a MyFitness ancestor supplied one; the coach and squad pages have none,
    // and both paths share the debouncer, so the duplicate costs nothing.
    'trickle_complete',
  ];
  const resyncDebouncer = debouncer(400);
  const onActivityEvent = () => resyncDebouncer.debounce(resync);

  onMount(() => {
    //@ts-ignore
    const channel = window.Xert?._userChannel;
    if (!channel) return;
    for (const event of liveEvents) channel.bind(event, onActivityEvent);
    return () => {
      for (const event of liveEvents) channel.unbind(event, onActivityEvent);
      resyncDebouncer.clear();
    };
  });

  // A trickle-down recalculation rewrites the fitness numbers on every activity, so reload once it
  // finishes rather than leaving stale XSS/difficulty on screen. Subscribed by hand because the
  // store is only there when a MyFitness ancestor supplied one.
  let wasTrickling = false;
  const stopWatchingTrickle = trickleActive?.subscribe((trickling) => {
    if (wasTrickling && !trickling) onActivityEvent();
    wasTrickling = trickling;
  });
  onDestroy(() => stopWatchingTrickle?.());

  // Tag edits don't raise a live event, so apply each saved change to the listed copy -- the card
  // and the details dialog each have their own editor, and one left showing old tags would look
  // wrong -- then drop the card if it no longer matches the filters that depend on tags, e.g. its
  // only matching tag was just removed. Done locally for the one card rather than by refetching,
  // which would miss a card below resync()'s window. (A tag being added can't make a card appear:
  // one that didn't match isn't listed.)
  const stopWatchingTags = activityTagsSaved.subscribe((change) => {
    if (!change) return;
    const edited = activities.find((a) => a.path === change.path);
    if (!edited) return;
    const tags = applyTagChange(edited.custom_tags ?? [], change);
    if (matchesTagFilters(edited, tags)) {
      updateActivity(edited, { custom_tags: tags });
    } else {
      removeActivity(edited);
    }
  });
  onDestroy(stopWatchingTags);

  /** Mirrors the tag-dependent parts of filterActivities() in helpers.php for one edited card. */
  function matchesTagFilters(exercise: Exercise, tags: string[]): boolean {
    if (fields.customTags.length && !fields.customTags.some((tag) => tags.includes(tag))) return false;
    // The Workouts pill: identified workouts (see whereIdentifiedWorkout()) or anything tagged Workout.
    if (fields.activityType.value === WORKOUTS_ACTIVITY_TYPE) {
      const workoutId: unknown = exercise.workout_id;
      const identified = workoutId != null && workoutId !== '' && workoutId !== '0' && workoutId !== 0;
      if (!identified && !tags.includes('Workout')) return false;
    }
    return true;
  }

  function removeActivity(exercise: Exercise) {
    activities = activities.filter((a) => a.path !== exercise.path);
    totalResults = Math.max(0, totalResults - 1);
  }

  function updateActivity(exercise: Exercise, changes: Partial<Exercise>) {
    activities = activities.map((a) => (a.path === exercise.path ? { ...a, ...changes } : a));
  }

  // ExerciseDialog's delete/hide actions write through this rather than taking a callback.
  const todaysActivities: TodaysActivities = {
    get activities() { return activities; },
    update: (updater) => { activities = updater(activities); },
  };

  let detailsOpen = false;
  // ExerciseDialog only renders its content while open, so the empty placeholder is never shown.
  let activityForDetails: Exercise = {};
  function openDetails(exercise: Exercise) {
    activityForDetails = exercise;
    detailsOpen = true;
  }

  // The dialog was handed a reference into the list, so a resync that replaces that activity would
  // otherwise leave it reading the numbers it opened on. Re-point it at the fresh copy; an activity
  // that is no longer in the list (deleted, or filtered out) keeps what the dialog already has,
  // which is what the athlete is still looking at.
  $: if (detailsOpen && activityForDetails.path) {
    const fresh = activities.find((activity) => activity.path === activityForDetails.path);
    if (fresh && fresh !== activityForDetails) activityForDetails = fresh;
  }
</script>

<dashboard class="xert-svelte h-full flex flex-row gap-4 border-solid border-0 border-t border-border">
  <ChooseActivityView
    bind:fields={fieldsProxy}
    {activities}
    {isLoading}
    {isLoadingMore}
    {isResyncing}
    {loadFailed}
    {totalResults}
    {hasMore}
    {stickyTop}
    {showOwner}
    {owners}
    {ownersTitle}
    {onScheduleActivity}
    onLoadMore={loadMore}
    onRefresh={onActivityEvent}
    onViewDetails={showOwner ? null : openDetails}
    onDelete={removeActivity}
    onChange={updateActivity}
  />
</dashboard>

{#if !showOwner}
  <ExerciseDialog
    bind:open={detailsOpen}
    exercise={activityForDetails}
    hideScheduleButtons
    {todaysActivities}
  />
{/if}
